import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { plugin as engagementPlugin } from '@amplitude/engagement-browser'
import store from '@/store'

interface IUserDetails {
    userRoleType: string | null
    userRole: string | null
    email: string | null
    userName: string | null
    company: string | null
    appVersion: string | null
    appflowVersion: string | null
}

const checkUserDetailsFromStoreIsAvailable = async (
    attempts = 0
): Promise<boolean> => {
    const authUser = store.getters['auth/getUser']
    const email = authUser?.email

    if (!email) {
        if (attempts < 3) {
            console.info(
                `[AMPLITUDE] Waiting for user info... Retry (${attempts + 1}/3)`
            )
            await new Promise((resolve) => setTimeout(resolve, 1000))
            return checkUserDetailsFromStoreIsAvailable(attempts + 1)
        } else {
            return false
        }
    }
    return true
}

// This function can be called from Desktop and Mobile,
// so its centralized and easier to maintain.
// If userDetailsObj and amplitudeToggle is passed, then it came from Mobile;
// this is due to Desktop and Mobile using different Vuex stores and this function
// not having access to Mobile store to check user details and company toggles.
export const initializeAmplitude = async (
    userDetailsObj: IUserDetails | null = null,
    amplitudeToggle = false
) => {
    const isAmplitudeOn =
        window?.ASKNICELY_COMPANY_VARS?.has_amplitude === 1 || amplitudeToggle
    if (!isAmplitudeOn) {
        console.error(
            '[AMPLITUDE] Failed to initialize: Toggle is not enabled.'
        )
        return false
    }

    const AMPLITUDE_API_KEY = process.env.VUE_APP_AMPLITUDE_API_KEY || ''
    if (!AMPLITUDE_API_KEY) {
        console.error(
            '[AMPLITUDE] Failed to initialize: AMPLITUDE_API_KEY is not set'
        )
        return false
    }

    const isStaff = document.getElementById('is-staff')?.innerText === '1'
    const isRunningLocal = process.env.VUE_APP_APP_ENV === 'local'
    const isForceAmplitude = process.env.VUE_APP_FORCE_AMPLITUDE === 'true'

    if ((!isRunningLocal && !isStaff) || isForceAmplitude) {
        try {
            let userRoleType
            let userRole
            let email
            let userName
            let company
            let appVersion = ''
            let appflowVersion = ''

            // If userDetailsObj is passed, then it came from mobile
            if (userDetailsObj !== null) {
                userRoleType = userDetailsObj?.userRoleType || ''
                userRole = userDetailsObj?.userRole || ''
                email = userDetailsObj?.email || ''
                userName = userDetailsObj?.userName || ''
                company = userDetailsObj?.company || ''
                appVersion = userDetailsObj?.appVersion || ''
                appflowVersion = userDetailsObj?.appflowVersion || ''
            } else {
                const isUserDetailsFromStoreAvailable =
                    await checkUserDetailsFromStoreIsAvailable()

                if (!isUserDetailsFromStoreAvailable) {
                    console.error(
                        '[AMPLITUDE] Failed to initialize: User details not available.'
                    )
                    return false
                }

                const authDetails = store.getters['auth/details']
                const authUser = store.getters['auth/getUser']
                email = authUser?.email
                userRole = authDetails?.userRole
                userRoleType = authDetails?.userRoleType
                userName = authUser?.name
                company = authDetails?.company?.domain
            }

            if (!email) {
                console.error(
                    '[AMPLITUDE] Failed to initialize: User email not available.'
                )
                return false
            }

            const sessionReplayTracking = sessionReplayPlugin()
            amplitude.add(sessionReplayTracking)
            amplitude.add(engagementPlugin())

            amplitude.init(AMPLITUDE_API_KEY, email, {
                fetchRemoteConfig: true,
                defaultTracking: false,
                autocapture: {
                    attribution: false,
                    pageViews: false,
                    sessions: false,
                    formInteractions: false,
                    fileDownloads: false,
                    elementInteractions: true,
                },
            })

            const identifyEvent = new amplitude.Identify()
            identifyEvent.set('User Role Type', userRoleType)
            identifyEvent.set('User Role', userRole)
            identifyEvent.set('Email', email)
            identifyEvent.set('Name', userName)
            identifyEvent.set('App Version', appVersion)
            identifyEvent.set('Appflow Version', appflowVersion)
            amplitude.identify(identifyEvent)
            amplitude.setGroup('Company', company)

            window.amplitudeInstance = amplitude
            return true
        } catch (error: unknown) {
            console.error(
                '[AMPLITUDE] Failed to initialize: ',
                error instanceof Error ? error.message : String(error)
            )
            return false
        }
    }
    return false
}

export const trackAmplitude = async (
    pageTitle: string | null = null,
    eventProperties: Record<string, unknown> = {},
    eventName = '[Amplitude] Page Viewed',
    isMobile = false
) => {
    if (!window.amplitudeInstance) {
        const isAmplitudeInitialized = await initializeAmplitude()

        if (!isAmplitudeInitialized) {
            console.error(
                '[AMPLITUDE] Failed to initialize when tracking event.'
            )
            return
        }
    }
    try {
        window.amplitudeInstance.track(eventName, {
            '[Amplitude] Page Title': pageTitle,
            'AN - Device Type': isMobile ? 'Mobile' : 'Desktop',
            ...eventProperties,
        })
    } catch (error) {
        console.error('[AMPLITUDE] Failed to track event:', error)
    }
}
